const sections = [
  {
    title: "1. Overview",
    body: [
      "This License explains the rights and restrictions that apply to the iBox software, website, desktop application, documentation, brand assets, and related materials.",
      "By downloading, installing, accessing, or using iBox, you agree to this License as part of the iBox Terms of Service.",
    ],
  },
  {
    title: "2. License to use iBox",
    body: [
      "Subject to your compliance with the Terms of Service and this License, iBox grants you a limited, revocable, non-exclusive, non-transferable, non-sublicensable license to download, install, access, and use iBox for your personal, team, league, business, or broadcast production purposes.",
      "This license allows you to use iBox to configure broadcast apps, create overlay graphics, run supported stream tools, enable audience-facing interactions, and display configured iBox content in your streams, broadcasts, recordings, live productions, and related content.",
    ],
  },
  {
    title: "3. Ownership",
    body: [
      "iBox and its owners retain all rights, title, and interest in and to the iBox software, source code, object code, user interface, visual design, workflows, documentation, website, logos, trademarks, service marks, trade dress, and other iBox materials.",
      "This License does not sell or transfer ownership of iBox to you. It only gives you permission to use iBox under the conditions described here.",
    ],
  },
  {
    title: "4. User content and uploaded assets",
    body: [
      "You keep ownership of the text, logos, images, fonts, textures, graphics, data sources, RSS feeds, social handles, and other content or assets you add to iBox.",
      "You grant iBox a limited license to process, transform, resize, crop, recolor, normalize, preview, store locally, and display your content and assets solely as needed to provide the features you choose to use.",
      "You are responsible for making sure you have the rights and permissions needed to use any content, logos, trademarks, images, fonts, or other assets you add to iBox.",
    ],
  },
  {
    title: "5. Broadcast and content output",
    body: [
      "You may display graphics, overlays, widgets, browser-source outputs, and other content generated or configured with iBox in your live streams, broadcasts, videos, clips, recordings, social posts, league productions, event productions, and related media.",
      "This permission does not grant you ownership of iBox itself, the iBox brand, or iBox software. It only permits the normal use and display of configured iBox output as part of your content.",
    ],
  },
  {
    title: "6. Restrictions",
    body: [
      "You may not copy, modify, distribute, sell, rent, lease, sublicense, host, resell, or commercially exploit iBox except as expressly allowed by this License or with written permission from iBox.",
      "You may not reverse engineer, decompile, disassemble, attempt to derive source code from, bypass technical restrictions in, remove license checks from, or create competing products from iBox except to the extent applicable law prohibits those restrictions.",
      "You may not use iBox branding, logos, names, or visual identity in a way that suggests sponsorship, endorsement, partnership, or ownership by iBox without written permission.",
    ],
  },
  {
    title: "7. iBox branding and attribution",
    body: [
      "The iBox name, logos, marks, and brand assets are owned by iBox. You may not use them outside the normal operation of iBox without written permission.",
      "Certain iBox features or plans may include attribution, such as \"Powered by iBox\" or similar branding. You may not remove, hide, alter, or bypass required attribution unless the applicable iBox plan or feature expressly allows it.",
    ],
  },
  {
    title: "8. Third-party materials",
    body: [
      "iBox may include or interact with third-party software, libraries, APIs, fonts, icons, services, data sources, feeds, or other materials. Third-party materials may be subject to separate licenses, terms, or policies.",
      "Your use of third-party materials through iBox does not give you rights beyond those provided by the applicable third party.",
    ],
  },
  {
    title: "9. Open-source components",
    body: [
      "iBox may include open-source components. Those components are licensed under their respective open-source licenses.",
      "Nothing in this License is intended to limit rights you may have under applicable open-source licenses for those components.",
    ],
  },
  {
    title: "10. Fonts, images, and third-party logos",
    body: [
      "If you add fonts, images, logos, trademarks, or other third-party assets to iBox, you are responsible for complying with the licenses and permissions that apply to those assets.",
      "iBox does not grant you permission to use third-party brands, logos, fonts, images, or data sources that you do not otherwise have the right to use.",
    ],
  },
  {
    title: "11. Updates and changes",
    body: [
      "iBox may provide updates, patches, feature changes, or new versions. This License applies to updates unless a separate license is provided with a specific update.",
      "Some updates may be required for continued compatibility, security, or access to certain features.",
    ],
  },
  {
    title: "12. Termination",
    body: [
      "This License remains in effect until terminated. Your rights under this License automatically terminate if you violate the Terms of Service or this License.",
      "After termination, you must stop using iBox and delete any unauthorized copies of the software. Sections that by their nature should survive termination will continue to apply, including ownership, restrictions, branding, third-party materials, disclaimers, and limitation of liability provisions.",
    ],
  },
  {
    title: "13. No warranties",
    body: [
      "iBox is licensed, not sold, and is provided on an \"as is\" and \"as available\" basis. To the fullest extent allowed by law, iBox disclaims all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, non-infringement, compatibility, availability, and reliability.",
    ],
  },
  {
    title: "14. Contact",
    body: [
      "Questions about this License can be sent through the contact page on the iBox website.",
    ],
  },
];

export function LicensePage() {
  return (
    <main className="relative min-h-screen overflow-hidden pt-16">
      <div className="download-bg absolute inset-0 z-0 bg-bg">
        <div className="absolute inset-0 bg-gt-grid opacity-35" />
        <div className="absolute inset-0 bg-bg/40" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent to-bg" />
      </div>

      <section className="relative z-10 mx-auto max-w-4xl px-6 py-24">
        <div className="mb-12">
          <div className="mb-6 flex items-center gap-3">
            <div className="h-px w-10 bg-primary" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-primary">Legal</span>
          </div>
          <h1 className="font-display text-6xl font-bold uppercase leading-[0.9] text-text md:text-8xl">License</h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted md:text-lg">
            Last updated: May 26, 2026
          </p>
        </div>

        <div className="rounded-xl border border-border bg-surface/92 shadow-2xl shadow-black/20">
          <div className="border-b border-border p-6 md:p-8">
            <p className="text-base leading-relaxed text-muted">
              This License explains what you can do with iBox, what remains owned by iBox, and what responsibilities apply to content you bring into the app.
            </p>
          </div>
          <div className="divide-y divide-border">
            {sections.map((section) => (
              <section key={section.title} className="p-6 md:p-8">
                <h2 className="font-display text-2xl font-bold uppercase leading-none text-text md:text-3xl">{section.title}</h2>
                <div className="mt-4 space-y-4">
                  {section.body.map((paragraph) => (
                    <p key={paragraph} className="text-sm leading-7 text-muted md:text-base">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
